import { apiUrl } from 'shared/config/api'
import { reportIpNotAllowedFromResponse } from 'shared/auth/ipNotAllowed'

/**
 * CSRF トークンの取得・保持を担う transport 非依存モジュール。
 *
 * Cookie はブラウザ単位で共有されるため、ここが fetch でトークンを再発行させれば
 * axios 経由のリクエストにも同じ Cookie が効く。axios を廃止するときにこのファイルは
 * 変更不要で、`apiFetch` からそのまま使い続けられる。
 */

const CSRF_COOKIE_NAME = 'CSRF-TOKEN'

/** API 側 (`ApplicationController#set_csrf_token`) が検証するリクエストヘッダ名。 */
export const CSRF_HEADER_NAME = 'X-CSRF-Token'

/** CSRF トークン Cookie を再発行させるエンドポイント。 */
export const CSRF_ENDPOINT = '/v1/sessions'

/** Rails 側で CSRF 検証されるメソッド。GET / HEAD / OPTIONS は検証対象外。 */
const CSRF_REQUIRED_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/** CUD かどうか(= トークンが必要かどうか)を判定する。method 未指定は GET 扱い。 */
export const requiresCsrfToken = (method: string | undefined): boolean =>
  CSRF_REQUIRED_METHODS.has((method ?? 'GET').toUpperCase())

/**
 * CSRF トークン Cookie を読む。
 *
 * API 側は Cookie 値を URL エスケープして返すため decode が必要。axios の xsrf 実装
 * (`decodeURIComponent`) と同じ扱いにして、apiFetch への移行前後で値を変えない。
 */
export const readCsrfToken = (): string | null => {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=([^;]*)`)
  )
  return match == null ? null : decodeURIComponent(match[1])
}

/**
 * `/v1/sessions` を叩いて CSRF トークン Cookie を再発行させる。
 *
 * axios ではなく fetch を使うのは、apiClient の request interceptor から呼んでも
 * 自己再帰しないようにするためと、axios 廃止後もこのファイルを触らずに済ませるため。
 */
export const fetchCsrfToken = async (): Promise<void> => {
  const res = await fetch(apiUrl(CSRF_ENDPOINT), {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  })
  await reportIpNotAllowedFromResponse(res)
  if (!res.ok) {
    throw new Error(`[csrf] GET ${CSRF_ENDPOINT} -> ${res.status} ${res.statusText}`)
  }
}

/** 進行中の取得。同時に走る CUD が `/v1/sessions` を多重に叩かないよう共有する。 */
let inflight: Promise<void> | null = null

/**
 * CUD の直前にトークンを取り直す。
 *
 * SPA では初回ロード以降 `/v1/sessions` が呼ばれないため、Cookie を失った状態のまま
 * CSRF エラー(API 側 500)になり続ける。同時実行分は進行中の取得を共有して 1 回にまとめる。
 */
export const ensureFreshCsrfToken = async (): Promise<void> => {
  inflight ??= fetchCsrfToken().finally(() => {
    inflight = null
  })
  await inflight
}
